import type { BlockTableData, TableCellMerge, TableDiff } from '../types';

/**
 * This function checks if the visibility of the table header or card title has changed
 * and stores the changes in the diff object passed to it.
 */
export function diffTableHeaderOrTitleVisibility(
    beforeData: BlockTableData,
    afterData: BlockTableData,
    diff: TableDiff
): void {
    // If the table header visibility has changed, we need to store it on the diff.
    if (
        'hideHeader' in beforeData.view &&
        'hideHeader' in afterData.view &&
        beforeData.view.hideHeader !== afterData.view.hideHeader
    ) {
        diff.view.hideHeader = {
            before: beforeData.view.hideHeader,
            after: afterData.view.hideHeader,
        };
    }

    if (beforeData.view.type === 'grid' && afterData.view.type === 'grid') {
        const beforeStickyHeader = beforeData.view.stickyHeader ?? false;
        const afterStickyHeader = afterData.view.stickyHeader ?? false;
        const beforeStickyFirstColumn = beforeData.view.stickyFirstColumn ?? false;
        const afterStickyFirstColumn = afterData.view.stickyFirstColumn ?? false;

        if (beforeStickyHeader !== afterStickyHeader) {
            diff.view.stickyHeader = {
                before: beforeStickyHeader,
                after: afterStickyHeader,
            };
        }

        if (beforeStickyFirstColumn !== afterStickyFirstColumn) {
            diff.view.stickyFirstColumn = {
                before: beforeStickyFirstColumn,
                after: afterStickyFirstColumn,
            };
        }
    }

    if (!areTableCellMergesEqual(beforeData.cellMerges, afterData.cellMerges)) {
        diff.view.cellMerges = {
            before: beforeData.cellMerges,
            after: afterData.cellMerges,
        };
    }

    // If the card title visibility has changed, we need to store it on the diff.
    if ('hideColumnTitle' in beforeData.view && 'hideColumnTitle' in afterData.view) {
        const prevHideColumnTitle = beforeData.view.hideColumnTitle ?? false;
        const hideColumnTitle = afterData.view.hideColumnTitle ?? false;
        if (prevHideColumnTitle !== hideColumnTitle) {
            diff.view.hideColumnTitle = {
                before: prevHideColumnTitle,
                after: hideColumnTitle,
            };
        }
    }
}

/**
 * This function compares the definitions of a table before and after an edit,
 * and stores any changes in the diff object passed to it.
 */
export function diffTableDefinitions(
    beforeData: BlockTableData,
    afterData: BlockTableData,
    diff: TableDiff
): TableDiff {
    Object.entries(afterData.definition).forEach(([key, afterDefinition]) => {
        const beforeDefinition = beforeData.definition[key];
        if (beforeDefinition) {
            // If the definition has changed, we need to store it on the diff.
            const titleChanged = beforeDefinition.title !== afterDefinition.title;
            const typeChanged = beforeDefinition.type !== afterDefinition.type;
            const textAlignmentChanged =
                beforeDefinition.type === 'text' &&
                afterDefinition.type === 'text' &&
                beforeDefinition.textAlignment !== afterDefinition.textAlignment;
            const verticalAlignmentChanged =
                beforeDefinition.type === 'text' &&
                afterDefinition.type === 'text' &&
                beforeDefinition.verticalAlignment !== afterDefinition.verticalAlignment;
            const isDefinitionChanged =
                titleChanged || typeChanged || textAlignmentChanged || verticalAlignmentChanged;

            if (isDefinitionChanged) {
                diff.definitions.set(key, {
                    before: beforeDefinition,
                    after: afterDefinition,
                });
            }
        } else {
            // If the definition is new, we need to store it on the diff.
            diff.definitions.set(key, {
                before: undefined,
                after: afterDefinition,
            });
        }
    });

    return diff;
}

/** Compare table cell merges without treating their storage order as meaningful. */
export function areTableCellMergesEqual(
    before: readonly TableCellMerge[] | undefined,
    after: readonly TableCellMerge[] | undefined
): boolean {
    if (before === after) return true;
    if (!before || !after || before.length !== after.length) return false;

    const counts = new Map<string, number>();
    for (const merge of before) {
        const identity = getTableCellMergeIdentity(merge);
        counts.set(identity, (counts.get(identity) ?? 0) + 1);
    }

    for (const merge of after) {
        const identity = getTableCellMergeIdentity(merge);
        const count = counts.get(identity);
        if (!count) return false;
        counts.set(identity, count - 1);
    }

    // Both sides have the same length, so consuming every entry of `after` exhausts `before`.
    return true;
}

/** Length-prefix the record id so a separator inside an id cannot forge another identity. */
function getTableCellMergeIdentity(merge: TableCellMerge): string {
    const { record, column } = merge.anchor;
    return `${record.length}:${record}:${column}:${merge.rowSpan}:${merge.colSpan}`;
}
